import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { z } from "zod";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  KEY_MODIFIER_COMMAND,
} from "lexical";
import { FormProvider } from "react-hook-form";

import { FormInput, useBaseForm } from "components/BaseForm";
import { isSafeUrl, isUrl } from "components/helpers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldGroup } from "@/components/ui/field";

// A bare host name is the common case, so it keeps the editor's long-standing
// http:// default rather than being rejected as protocol-less.
const withDefaultProtocol = url => (isUrl(url) ? url : `http://${url}`);

const schema = z.object({
  url: z
    .string()
    .trim()
    .min(1, "URL is required")
    .refine(value => isSafeUrl(withDefaultProtocol(value)), {
      message: "Enter an http, https, mailto or tel URL",
    }),
});

// Not a BaseForm: BaseForm submits only from a button click, so Enter in the
// URL field would not apply the link, and changing that affects every form.
function LinkForm({ initialUrl, onSubmit, onCancel }) {
  const methods = useBaseForm({ schema, defaultValues: { url: initialUrl } });

  // The dialog is portaled into the enclosing form's React tree, so Enter and
  // submit events would otherwise bubble to it: a form that ignores Enter
  // would cancel this submit, and a note form would save the whole note.
  const handleKeyDown = event => {
    if (event.key === "Enter") event.stopPropagation();
  };

  const handleFormSubmit = event => {
    event.stopPropagation();
    methods.handleSubmit(onSubmit)(event);
  };

  return (
    <FormProvider {...methods}>
      <form onKeyDown={handleKeyDown} onSubmit={handleFormSubmit} noValidate>
        <FieldGroup>
          <FormInput
            name="url"
            label="URL"
            placeholder="example.com"
            autoFocus
          />
        </FieldGroup>
        <DialogFooter className="tw:mt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">{initialUrl ? "Save Link" : "Add Link"}</Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
}

LinkForm.propTypes = {
  initialUrl: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function LinkDialogPlugin({ open, onOpenChange }) {
  const [editor] = useLexicalComposerContext();
  const [initialUrl, setInitialUrl] = useState("");
  // TOGGLE_LINK_COMMAND acts on the selection, and focusing the dialog input
  // clears it, so the selection is captured before the dialog opens.
  const savedSelection = useRef(null);

  const captureSelection = useCallback(
    () =>
      editor.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          savedSelection.current = null;
          setInitialUrl("");
          return false;
        }
        // Recorded even when collapsed, so a dialog opened from the toolbar
        // applies to the caret rather than to a selection left from last time.
        savedSelection.current = selection.clone();
        const link = $findMatchingParent(
          selection.anchor.getNode(),
          $isLinkNode
        );
        setInitialUrl(link ? link.getURL() : "");
        // Cmd+K makes a link out of the selected text, so it needs a selection;
        // the toolbar button opens regardless and only needs the URL.
        return !selection.isCollapsed();
      }),
    [editor]
  );

  useEffect(() => {
    if (open) {
      captureSelection();
    }
  }, [open, captureSelection]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          KEY_MODIFIER_COMMAND,
          event => {
            if (event.key !== "k" || !(event.metaKey || event.ctrlKey)) {
              return false;
            }
            if (!captureSelection()) {
              return false;
            }
            event.preventDefault();
            onOpenChange(true);
            return true;
          },
          COMMAND_PRIORITY_NORMAL
        )
      ),
    [editor, captureSelection, onOpenChange]
  );

  const handleSubmit = ({ url }) => {
    const href = withDefaultProtocol(url.trim());
    editor.update(() => {
      if (savedSelection.current) {
        $setSelection(savedSelection.current.clone());
      }
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, href);
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialUrl ? "Edit Link" : "Add Link"}</DialogTitle>
        </DialogHeader>
        <LinkForm
          // react-hook-form reads defaultValues once, and the URL is resolved
          // by an effect after the dialog has mounted, so the form is rebuilt
          // when it arrives rather than keeping the empty default.
          key={initialUrl}
          initialUrl={initialUrl}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

LinkDialogPlugin.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};

export default LinkDialogPlugin;
